import { gql } from '@apollo/client'
import React from 'react'
import styled from 'styled-components'

import { Nullable, isProviderUser } from 'shared/utils/type-guards'

import { COLORS, SIZES } from '~/common/globalStyles'
import { UserProjectOrganizationTypeEnumType } from '~/common/types/graphql'
import { CardBody, CardHeader } from '~/views/components/elements/overview'
import Link from '~/views/components/routing/Link'
import Avatar from '~/views/components/ui/Avatar'
import Card from '~/views/components/ui/Card'
import RichTextView from '~/views/components/ui/RichTextView'
import Text from '~/views/components/ui/Text'
import { useCurrentUserContext } from '~/views/contexts/CurrentUserContext'
import useProjectId from '~/views/hooks/useProjectId'
import { useStaticLabels } from 'shared/hooks/use-static-labels'

const InfoColumn = styled.div`
  margin-left: ${({ theme }) => theme.spacing(3)};
  display: flex;
  flex-direction: column;
`

const ProjectManagerContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const TextRow = styled(Text)`
  margin-bottom: 1px;
  &:last-child {
    margin-top: ${({ theme }) => theme.spacing(2)};
  }
`

const NoteContainer = styled.div(
  ({ hidden, theme }) => `
  display: flex;
  flex-direction: column;
  background-color: ${hidden ? COLORS.beige : 'transparent'};
  margin-top: ${theme.spacing(2)};
  padding: ${hidden ? theme.spacing(2) : 0}px;
  border-radius: ${theme.shape.borderRadius}px;
`,
)

const EditNoteButton: React.FC<{ label: string; className?: string }> = ({
  className,
  label,
}) => {
  const projectId = useProjectId()
  return (
    <Link
      className={className}
      href={{
        query: { 'edit-project': true, 'task-tab': 'pm-note', projectId },
      }}
    >
      <Text color="blue" weight="normal">
        {label}
      </Text>
    </Link>
  )
}

const StyledEditNoteButton = styled(EditNoteButton)`
  margin-left: ${({ theme }) => theme.spacing(2)};
`

export interface Props {
  className?: string
  project?: {
    id: string
    pmDisplayRole: Nullable<string>
    pmNote: Nullable<string>
    pmNoteInternalOnly?: Nullable<boolean>
    currentUserProjectContext?: Nullable<UserProjectOrganizationTypeEnumType>
    projectManager?: Nullable<{
      id: string
      firstName?: Nullable<string>
      lastName?: Nullable<string>
      calendarLink?: Nullable<string>
      phone?: Nullable<string>
      email?: Nullable<string>
      bio?: Nullable<string>
      avatar?: Nullable<{ originalUrl: string }>
    }>
  }
}

const ProjectManagerCard = ({ className, project }: Props) => {
  const [getCommonLabel, getProjectOverviewLabel] = useStaticLabels([
    'common',
    'projectOverview',
  ])

  const projectManager = project?.projectManager
  const pmNote = project?.pmNote
  const pmNoteInternalOnly = project?.pmNoteInternalOnly
  const currentUserProjectContext = project?.currentUserProjectContext

  const { currentUser } = useCurrentUserContext()
  const isPM = currentUser ? currentUser.id === projectManager?.id : false
  const showNote = pmNoteInternalOnly
    ? isProviderUser(currentUserProjectContext)
    : true
  const calendarLink = projectManager?.calendarLink || ''
  const formattedCalendarLink = calendarLink.startsWith('https://')
    ? calendarLink
    : `https://${calendarLink}`

  if (!project || !projectManager) return null

  return (
    <Card className={className}>
      <CardHeader>
        <Text size="large" weight="bold">
          {project.pmDisplayRole}
        </Text>
      </CardHeader>

      <CardBody>
        <Avatar
          size="120px"
          name1={projectManager?.firstName || ''}
          name2={projectManager?.lastName || ''}
          fontSize={SIZES.huge}
          imageUrl={project.projectManager?.avatar?.originalUrl || ''}
          style={{ alignSelf: 'flex-start' }}
        />

        <InfoColumn>
          <ProjectManagerContainer>
            <TextRow weight="bold">
              {`${projectManager.firstName} ${projectManager.lastName}`}
            </TextRow>
            <TextRow>{projectManager.phone}</TextRow>
            <TextRow>{projectManager.email}</TextRow>
            <TextRow data-testid="pmcard-bio">{projectManager.bio}</TextRow>
            {projectManager.calendarLink && (
              <TextRow>
                <a
                  href={formattedCalendarLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {getProjectOverviewLabel(
                    'projectOverviewScheduleTimeWithThePm',
                  )}
                </a>
              </TextRow>
            )}
          </ProjectManagerContainer>

          {(showNote || isPM) && (
            <NoteContainer hidden={Boolean(pmNoteInternalOnly)}>
              <Text weight="bold">
                {`${getProjectOverviewLabel('projectOverviewNoteFromThe')} ${project.pmDisplayRole}`}
                {pmNote && isPM && (
                  <StyledEditNoteButton label={getCommonLabel('edit')} />
                )}
              </Text>
              {!pmNote && isPM && (
                <EditNoteButton
                  label={getProjectOverviewLabel('projectOverviewAddYourNote')}
                />
              )}
              {pmNote && <RichTextView value={pmNote} dataTestId="pm-note" />}
            </NoteContainer>
          )}
        </InfoColumn>
      </CardBody>
    </Card>
  )
}

ProjectManagerCard.fragments = {
  project: gql`
    fragment ProjectManagerCard_Project on ProjectType {
      id
      pmDisplayRole
      pmNote
      pmNoteInternalOnly
      currentUserProjectContext
      projectManager {
        id
        firstName
        calendarLink
        lastName
        phone
        email
        bio
        avatar {
          originalUrl
        }
      }
    }
  `,
}

export default ProjectManagerCard
